import { create } from 'zustand';
import type { GameEvent, StoredGameEvent } from '#shared/schemas/event';
import type { Game, GamePlayer } from '#shared/schemas/game';
import { kvDel, kvGet, kvSet } from '@/lib/idb';

/**
 * Per-game client state, shared by the keeper pad, the board, the summary and the outbox bridge.
 *
 * - `server`: events the server has (pull, SSE snapshot and events).
 * - `local`: events written on THIS phone. They stay here until the outbox says the server has
 *   them, because the outbox is a transit queue and the keeper's fold must survive a reload with no
 *   signal. The slot (game, roster, local events, seq) is saved to IndexedDB (`games:slot:<id>`).
 * - The fold is `server` plus `local` by event id, so a play shows at once and never twice.
 *
 * Nothing in here talks to the network. `live.ts` feeds it, `bridge.ts` reconciles the outbox.
 */

export type Connection = 'idle' | 'connecting' | 'live' | 'down';

export interface Slot {
  game: Game | null;
  players: GamePlayer[];
  server: Record<string, StoredGameEvent>;
  local: Record<string, GameEvent>;
  /** Last seq this phone used for this game (per device per game, from 1). */
  seq: number;
  serverSeq: number;
  loaded: boolean;
  /** The server answered for this game (the create landed, or it came from the server). */
  known: boolean;
  connection: Connection;
}

const empty = (): Slot => ({
  game: null,
  players: [],
  server: {},
  local: {},
  seq: 0,
  serverSeq: 0,
  loaded: false,
  known: false,
  connection: 'idle',
});

interface Saved {
  game: Game | null;
  players: GamePlayer[];
  local: Record<string, GameEvent>;
  seq: number;
}

const slotKey = (id: string) => `games:slot:${id}`;
const ACTIVE_KEY = 'games:active';

interface GamesState {
  slots: Record<string, Slot>;
  load(id: string): Promise<void>;
  setGame(id: string, game: Game, players: GamePlayer[], known?: boolean): void;
  patchGame(id: string, patch: Partial<Game>): void;
  addLocal(id: string, events: GameEvent[], seq: number): void;
  dropLocal(id: string, ids: readonly string[]): void;
  /** Server events arrive (pull or SSE). They also clear the same ids from `local`. */
  ingest(id: string, events: readonly StoredGameEvent[]): void;
  replaceServer(id: string, events: readonly StoredGameEvent[], serverSeq: number): void;
  setConnection(id: string, c: Connection): void;
  forget(id: string): void;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();

/** Save the slot once per tick, from the latest state at write time (a burst of plays saves once). */
function scheduleSave(id: string): void {
  if (timers.has(id)) return;
  timers.set(
    id,
    setTimeout(() => {
      timers.delete(id);
      const s = useGameStore.getState().slots[id];
      if (!s) return;
      const saved: Saved = { game: s.game, players: s.players, local: s.local, seq: s.seq };
      void kvSet(slotKey(id), saved).catch(() => {});
    }, 0),
  );
}

export const useGameStore = create<GamesState>((set, get) => {
  const update = (id: string, fn: (s: Slot) => Slot, save = true) => {
    const cur = get().slots[id] ?? empty();
    const next = fn(cur);
    set({ slots: { ...get().slots, [id]: next } });
    if (save) scheduleSave(id);
  };

  return {
    slots: {},
    async load(id) {
      if (get().slots[id]?.loaded) return;
      let saved: Saved | undefined;
      try {
        saved = await kvGet<Saved>(slotKey(id));
      } catch {
        saved = undefined;
      }
      update(
        id,
        (s) => ({
          ...s,
          loaded: true,
          game: s.game ?? saved?.game ?? null,
          players: s.players.length ? s.players : (saved?.players ?? []),
          local: { ...(saved?.local ?? {}), ...s.local },
          seq: Math.max(s.seq, saved?.seq ?? 0),
        }),
        false,
      );
    },
    setGame(id, game, players, known = true) {
      update(id, (s) => ({ ...s, game, players, known: s.known || known }));
    },
    patchGame(id, patch) {
      update(id, (s) => (s.game ? { ...s, game: { ...s.game, ...patch } } : s));
    },
    addLocal(id, events, seq) {
      update(id, (s) => {
        const local = { ...s.local };
        for (const e of events) local[e.id] = e;
        return { ...s, local, seq: Math.max(s.seq, seq) };
      });
    },
    dropLocal(id, ids) {
      update(id, (s) => {
        const local = { ...s.local };
        for (const x of ids) delete local[x];
        return { ...s, local };
      });
    },
    ingest(id, events) {
      if (events.length === 0) return;
      update(id, (s) => {
        const server = { ...s.server };
        const local = { ...s.local };
        let top = s.serverSeq;
        for (const e of events) {
          server[e.id] = e;
          delete local[e.id];
          top = Math.max(top, e.serverSeq);
        }
        return { ...s, server, local, serverSeq: top };
      });
    },
    replaceServer(id, events, serverSeq) {
      update(id, (s) => {
        const server: Record<string, StoredGameEvent> = {};
        const local = { ...s.local };
        for (const e of events) {
          server[e.id] = e;
          delete local[e.id];
        }
        return { ...s, server, local, serverSeq };
      });
    },
    setConnection(id, c) {
      update(id, (s) => (s.connection === c ? s : { ...s, connection: c }), false);
    },
    forget(id) {
      const slots = { ...get().slots };
      delete slots[id];
      set({ slots });
      void kvDel(slotKey(id)).catch(() => {});
    },
  };
});

/** Every event the phone knows for a game: the server's, plus the ones still on their way. */
export function allEvents(s: Pick<Slot, 'server' | 'local'>): GameEvent[] {
  const out: Record<string, GameEvent> = { ...s.local };
  for (const [id, e] of Object.entries(s.server)) out[id] = e;
  return Object.values(out);
}

// ---------------------------------------------------------------- games this phone is keeping

export async function activeGameIds(): Promise<string[]> {
  try {
    return (await kvGet<string[]>(ACTIVE_KEY)) ?? [];
  } catch {
    return [];
  }
}

export async function markActive(id: string): Promise<void> {
  try {
    const cur = await activeGameIds();
    if (!cur.includes(id)) await kvSet(ACTIVE_KEY, [...cur, id].slice(-6));
  } catch {
    // storage is a convenience here
  }
}

export async function unmarkActive(id: string): Promise<void> {
  try {
    const cur = await activeGameIds();
    if (cur.includes(id))
      await kvSet(
        ACTIVE_KEY,
        cur.filter((x) => x !== id),
      );
  } catch {
    // ignore
  }
}
